import { useEffect, useState } from "react";

interface NavLink {
  label: string;
  /** 스크롤 대상 섹션의 id ("" 이면 페이지 최상단으로 이동) */
  target: string;
  render?: () => React.ReactNode;
}

const navLinks: NavLink[] = [
  {
    label: "하수구 원킬케어",
    target: "services",
    render: () => (
      <span>
        하수구 <span className="text-[#043fff]">원킬</span>
        <span className="text-[#FF0000]">케어</span>
      </span>
    ),
  },
  {
    label: "BLOG작업사례",
    target: "blog",
  },
  {
    label: "365일 긴급출동",
    target: "emergency",
  },
  { label: "고객감동 REVIEW", target: "blog" },
];

/** 고정 헤더 높이를 고려해 해당 섹션으로 부드럽게 스크롤한다. */
const scrollToSection = (target: string) => {
  if (typeof window === "undefined") return;
  if (!target) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const el = document.getElementById(target);
  if (!el) return;
  const header = document.querySelector("header");
  const offset = header ? header.offsetHeight : 0;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: "smooth" });
};

export default function SMHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 z-50 w-full bg-white transition-colors duration-300 ${
        scrolled ? "lg:bg-white lg:shadow-md" : "lg:bg-transparent"
      }`}
    >
      <div className="mx-auto flex px-[calc((100%-var(--container))/3)] items-center lg:items-start justify-between gap-4 py-3 md:py-4">
        {/* Logo */}
        <a href="/" className="flex shrink-0 items-center">
          <img
            src="/images/logo.png"
            alt="하수구원킬케어 로고"
            className="h-12 w-auto lg:h-12"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden flex-1 items-center justify-center gap-8 lg:flex xl:gap-12 pt-4">
          {navLinks.map((link) => (
            <button
              key={link.label}
              type="button"
              onClick={() => scrollToSection(link.target)}
              className="whitespace-nowrap text-[15px] font-medium text-gray-800 transition-colors hover:opacity-70 xl:text-base"
            >
              {link.render ? link.render() : link.label}
            </button>
          ))}
        </nav>

        {/* Phone banner (desktop) */}
        <a href="tel:010-8123-8285" className="hidden shrink-0 lg:block">
          <img
            src={"/images/num.png"}
            alt="무료견적 365일 24시간 긴급출동 010.8123.8285"
            className="h-9 w-auto lg:h-10 xl:h-12"
          />
        </a>

        {/* Hamburger toggle — mobile/tablet only */}
        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label={menuOpen ? "메뉴 닫기" : "메뉴 열기"}
          aria-expanded={menuOpen}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded lg:hidden"
        >
          <span className="relative block h-4 w-6">
            <span
              className={`absolute left-0 top-0 block h-0.5 w-6 bg-gray-700 transition-transform ${
                menuOpen ? "translate-y-[7px] rotate-45" : ""
              }`}
            />
            <span
              className={`absolute left-0 top-[7px] block h-0.5 w-6 bg-gray-700 transition-opacity ${
                menuOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute left-0 top-[14px] block h-0.5 w-6 bg-gray-700 transition-transform ${
                menuOpen ? "-translate-y-[7px] -rotate-45" : ""
              }`}
            />
          </span>
        </button>
      </div>

      {/* Mobile/tablet nav — smooth collapsible dropdown */}
      <nav
        className={`overflow-hidden border-t border-gray-200 bg-gray-50 transition-[max-height,opacity] duration-300 ease-in-out lg:hidden ${
          menuOpen ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <ul className="mx-auto flex max-w-[1300px] flex-col divide-y divide-gray-200 px-4 text-sm">
          {navLinks.map((link, index) => (
            <li
              key={link.label}
              className={`transition-all duration-300 ease-in-out ${
                menuOpen
                  ? "translate-y-0 opacity-100"
                  : "-translate-y-1 opacity-0"
              }`}
              style={{ transitionDelay: menuOpen ? `${index * 40}ms` : "0ms" }}
            >
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  scrollToSection(link.target);
                }}
                className="block w-full py-3 text-left font-medium text-gray-800"
              >
                {link.render ? link.render() : link.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
}
